import type { Passenger, PassengerCardProps } from '@fz/ui-components-react';
import { PassengerTypeEnum } from '@models/landing/ui/select-passenger-model';
import { getCurrentLanguage, getResource } from '@utils/resource-translator.util';
import { hasStandBy } from '@helper/passenger-status.util';

import {
  type NotCheckedInPax,
  type NotCheckedInPaxFlight,
  type NotCheckedInPaxFlightLeg,
} from '@models/ui/pnr-data-model';
import { isRtlLanguage, type Language } from '@models/i18n/language.model';
import { t } from 'i18next';

const BIDI_ISOLATE_START = '\u2067';
const BIDI_ISOLATE_END = '\u2069';

const isolateBidiText = (text: string): string => `${BIDI_ISOLATE_START}${text}${BIDI_ISOLATE_END}`;

const getRoute = (leg: NotCheckedInPaxFlightLeg): string =>
  getResource('SelectPassenger.Route.Text', {
    origin: leg.origin ?? '',
    destination: leg.destination ?? '',
  });

const INTERLINE_FZ_NOT_SELECTED_KEY = 'passengerList.seatNotSelected.Text';

export const getPassengerTypeLabel = (type: PassengerTypeEnum): string => {
  switch (type) {
    case PassengerTypeEnum.Adult:
      return getResource('Adult.Text');
    case PassengerTypeEnum.Child:
      return getResource('Child.Text');
    case PassengerTypeEnum.Infant:
      return getResource('Infant.Text');
    default:
      return '';
  }
};

const resolveStandByNotice = (pax: NotCheckedInPax): string => {
  if (!hasStandBy(pax.standByFlightsCount ?? 0)) {
    return getResource('CheckInOpen.Text');
  }
  if (pax.standByID90 === 1) {
    return getResource('StandById90.Text');
  }
  if (pax.standByWaitlist) {
    return getResource('StandbyWaitlist.Text');
  }
  return getResource('StandById90.Text');
};

const getFlightInfo = (
  _leg: NotCheckedInPaxFlightLeg,
  _flight: NotCheckedInPaxFlight,
  flightIndex: number,
  allLeg: NotCheckedInPaxFlightLeg[] | undefined,
): string => {
  const getInfokey =
    flightIndex === 1
      ? 'SelectPassenger.FlightInfo.Return.Text'
      : 'SelectPassenger.FlightInfo.Depart.Text';
  const result = allLeg?.map((item) => `${item?.operatingCarrier} ${item?.flightNum}`) ?? [];
  const origin = allLeg?.[0]?.origin ?? '';
  const destination = allLeg?.[allLeg.length - 1]?.destination ?? '';
  const isArabic = isRtlLanguage(getCurrentLanguage() as Language);
  const flightNumbers = isolateBidiText(
    isArabic ? [...result].reverse().join(' / ') : result.join(' / '),
  );
  const route = isolateBidiText(`${origin} ${getResource('To.Text')} ${destination}`);

  return `${getResource(getInfokey)} ${flightNumbers} - ${route}`;
};

const mapSeats = (
  route: string,
  leg: NotCheckedInPaxFlightLeg,
  totalLegs: number,
): {
  route: string;
  seatNumber: string | null;
  flightInfo: string;
  valueKey?: string;
  operatingCarrier?: string;
} => {
  const shouldShowNotSelected = !(leg.isInterline ?? false);
  const shouldUseNotSelectedValue = (leg.seatSSR ?? '') === '' && shouldShowNotSelected;
  const hasSeatNumber = Boolean(leg.seatSSR && leg.seatSSR.trim() !== '');
  const isSingleLeg = totalLegs === 1;

  // For one-way or single-leg RT with seat selected: show "Seat" label
  // For multi-leg: show route label ("DXB to TBS", etc.)
  const seatLabel = isSingleLeg && hasSeatNumber ? getResource('Seat.Text') : route;

  const seatData = {
    route: seatLabel,
    seatNumber: leg.seatSSR ?? null,
    flightInfo: 'SelectPassenger.Interline.Tooltip.Description.Text',
    valueKey: shouldUseNotSelectedValue ? getResource(INTERLINE_FZ_NOT_SELECTED_KEY) : undefined,
    operatingCarrier: leg.operatingCarrier,
  };
  return seatData;
};

const mapCheckedBaggage = (
  checkedBaggageSSR: string | undefined,
  addBaggageSSR: string | undefined,
): { included: string; additional?: string } | undefined => {
  const result: { included: string; additional?: string } = { included: '' };
  if (checkedBaggageSSR) {
    result.included = checkedBaggageSSR;
  }
  if (addBaggageSSR) {
    result.additional = addBaggageSSR;
  }
  return result;
};

const mapLegToFlight = (
  leg: NotCheckedInPaxFlightLeg,
  flight: NotCheckedInPaxFlight,
  inFant: boolean,
  associatedId: string,
  allLeg: NotCheckedInPaxFlightLeg[],
  flightIndex: number,
): {
  id: string;
  route: string;
  flightClass: string;
  seats: {
    route: string;
    seatNumber: string | null;
    flightInfo: string;
    valueKey?: string;
    operatingCarrier?: string;
  }[];
  handBaggageAllowance: string;
  checkedBaggageAllowance?: { included: string; additional?: string };
  flightInfo: string;
  isInterline: boolean;
  isInfant: boolean;
  associatedInfantID: string;
} => {
  const route = getRoute(leg);
  const flightInfo = getFlightInfo(leg, flight, flightIndex, allLeg);
  const isInterLine = allLeg?.some((item) => item.isInterline) ?? false;
  const seats = allLeg.map((item) => {
    const routeLeg = getRoute(item);
    return mapSeats(routeLeg, item, allLeg.length);
  });
  const handBaggageAllowance = leg.handBaggageSSR ?? '';
  const checkedBaggageAllowance = mapCheckedBaggage(
    leg.checkedBaggageSSR,
    leg.additionalCheckedBaggageSSR,
  );
  return {
    id: flight.logicalFlightID ?? '',
    route,
    flightClass: leg.flightClass ?? flight.flightClass ?? '',
    seats,
    handBaggageAllowance,
    checkedBaggageAllowance,
    flightInfo,
    isInterline: isInterLine,
    isInfant: inFant,
    associatedInfantID: associatedId,
  };
};

const mapFlights = (
  flights: NotCheckedInPaxFlight[] | undefined,
  infant: boolean,
  associatedInfantID: string,
): ReturnType<typeof mapLegToFlight>[] => {
  return (flights ?? []).flatMap(
    (flight, flightIndex) =>
      (flight.legs ?? [])
        .map((leg, index, allLeg) => {
          if (index === 0)
            return mapLegToFlight(leg, flight, infant, associatedInfantID, allLeg, flightIndex);
          return undefined;
        })
        .filter(Boolean) as ReturnType<typeof mapLegToFlight>[],
  );
};

const resolvePassengerDisplayName = (pax: NotCheckedInPax): string => {
  const trimmedName = pax.fullName?.trim();
  return trimmedName ? trimmedName : `${pax.firstName} ${pax.lastName}`.trim();
};

const resolveSkywardFields = (
  pax: NotCheckedInPax,
  canSelect: boolean,
  inFant: boolean,
): { skywardLabel: string; skywardValue: string } => {
  if (!canSelect || inFant) {
    return { skywardLabel: '', skywardValue: '' };
  }

  return {
    skywardLabel: `${t('PassengerEmiratesPrefix.Text')} ${t('SelectPassenger.OpenID.Text')}`,
    skywardValue: pax.skywardId
      ? `${pax.skywardId} ${t(`${pax.skywardTier?.toUpperCase()}.Text`)}`
      : '',
  };
};

const mapPassenger = (pax: NotCheckedInPax): Passenger => {
  const inFant = pax.passengerType === PassengerTypeEnum.Infant;
  const canSelect = pax.canSelectForCheckIn !== false;
  const { skywardLabel, skywardValue } = resolveSkywardFields(pax, canSelect, inFant);

  return {
    id: pax.paxId,
    name: resolvePassengerDisplayName(pax),
    type: getPassengerTypeLabel(pax.passengerType),
    isInfant: inFant,
    associatedInfantID: canSelect && !inFant ? (pax?.associatedInfantID ?? '') : '',
    flights: mapFlights(pax.flights, inFant, pax?.associatedInfantID),
    checkInStatus: canSelect ? getResource('CheckInOpen.Text') : resolveStandByNotice(pax),
    skywardLabel,
    skywardValue,
  };
};

export const mapNotCheckedInPassengers = (paxlist: NotCheckedInPax[] | undefined): Passenger[] => {
  return (paxlist ?? []).map(mapPassenger);
};

/** Selectable passengers only — Angular checkbox rows. */
export const filterSelectableNotCheckedInPassengers = (
  paxlist: NotCheckedInPax[] | undefined,
): NotCheckedInPax[] => (paxlist ?? []).filter((pax) => pax.canSelectForCheckIn !== false);

/**
 * Returns an array of passenger IDs for auto-selection:
 *   - [adultId] if only one adult (no children)
 *   - [adultId, infantId] if one adult and one infant
 *   - [] otherwise (multiple passengers, adult with child, etc.)
 *
 * @param passengers - Array of passengers with passengerType enum
 * @returns Array of passenger IDs to auto-select
 */
export function getSingleAdultOrAdultWithInfantId(
  passengers: { id: string; passengerType: PassengerTypeEnum }[],
): string[] {
  const ADULT_WITH_INFANT_PAIR = 2;
  // Use passengerType enum instead of translated type string for filtering
  // This works across all languages (English, Arabic, Russian, etc.)
  const adults = passengers.filter((p) => p.passengerType === PassengerTypeEnum.Adult);
  const infants = passengers.filter((p) => p.passengerType === PassengerTypeEnum.Infant);

  if (adults.length === 1 && infants.length === 0 && passengers.length < ADULT_WITH_INFANT_PAIR) {
    return [adults[0].id];
  }
  if (adults.length === 1 && infants.length === 1 && passengers.length === ADULT_WITH_INFANT_PAIR) {
    return [adults[0].id, infants[0].id];
  }
  return [];
}

const NOT_CHECKED_IN_STATUS_VARIANT = 'not-checked-in' as const;

/** Maps confirm-page not-checked-in passengers to DS PassengerCard props. */
export const mapNotCheckedInPaxToPassengerCards = (
  passengers: NotCheckedInPax[],
): PassengerCardProps[] =>
  passengers.map((passenger) => {
    const trimmedName = passenger.fullName?.trim();
    return {
      passenger: {
        name: trimmedName ? trimmedName : `${passenger.firstName} ${passenger.lastName}`.trim(),
        type: getPassengerTypeLabel(passenger.passengerType),
      },
      variant: 'notCheckedInPax',
      showStatus: resolveStandByNotice(passenger),
      showStatusVariant: NOT_CHECKED_IN_STATUS_VARIANT,
      'data-testid': `confirmation-not-checked-in-card-${passenger.paxId}`,
    };
  });

/** Angular: Check-in CTA when checkedInFlightsCount === 0 && canSelectForCheckIn. */
export const hasConfirmCheckInEligiblePassengers = (passengers: NotCheckedInPax[]): boolean =>
  passengers.some(
    (pax) => (pax.checkedInFlightsCount ?? 0) === 0 && pax.canSelectForCheckIn !== false,
  );
